// ─────────────────────────────────────────────────────────────────────────────
// FEATURE: Orders / Invoice  (route /orders/:ref/invoice — "Bill" on an active order, "Invoice" on a past one)
// A printable invoice for one order: seller, sequential invoice number (ZF-INV-<year>-<6 digits>, assigned by the backend
// when the order was created), invoice date, order id, billed-to / delivery address, an itemised table (catch, pack · cut,
// qty, rate, amount), the bill (items, delivery fee, welcome coupon, total paid), how and when it was paid (Razorpay
// payment id) and, for a cancelled order, the refund. "Print / save as PDF" uses the browser's print dialog; the
// site chrome is hidden in print (index.css).
// HONESTY: ZenFresh's GSTIN / company / FSSAI records are not published yet (data/legalRegistrations.ts is empty), so the
// page is titled "Order invoice", says it is not a GST tax invoice and prints no GSTIN. When those records are filled in
// the seller block, the "Tax invoice" title and the GSTIN/FSSAI lines appear on their own.
// ─────────────────────────────────────────────────────────────────────────────
import { useEffect, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowLeft, Printer } from "lucide-react";
import Breadcrumbs from "../components/Breadcrumbs";
import PageTransition from "../components/PageTransition";
import { packById } from "../data/packs";
import { COMPANY_REGISTRATION, FSSAI_LICENCES, GSTIN_RECORD } from "../data/legalRegistrations";
import useAuth from "../hooks/useAuth";
import useDocumentTitle from "../hooks/useDocumentTitle";
import { ApiError } from "../utils/api";
import { fetchOrder, formatWhen, paidVia, reachedAt, type Order } from "../utils/orders";
import { formatInr } from "../utils/priceFormat";

type Load = { status: "loading" } | { status: "missing" } | { status: "error" } | { status: "ready"; order: Order };

export default function OrderInvoice() {
  const { ref = "" } = useParams();
  const auth = useAuth();
  const signedIn = auth.status === "signed-in";
  const [load, setLoad] = useState<Load>({ status: "loading" });
  useDocumentTitle(`Invoice ${ref}`);

  useEffect(() => {
    if (!signedIn) return;
    fetchOrder(ref).then(
      (order) => setLoad({ status: "ready", order }),
      (error) => setLoad({ status: error instanceof ApiError && error.status === 404 ? "missing" : "error" }),
    );
  }, [signedIn, ref]);

  if (auth.status === "signed-out") return <Navigate to="/welcome" replace />;

  const shell = (children: React.ReactNode) => (
    <PageTransition>
      <div className="bg-foam-50 pb-24 pt-28 dark:bg-ocean-950 sm:pt-32 print:bg-white print:pb-0 print:pt-0">
        <div className="print:hidden"><Breadcrumbs dynamicLabel={ref} /></div>
        <div className="mx-auto flex max-w-3xl flex-col gap-4 px-4 pt-2 print:max-w-none print:px-0">{children}</div>
      </div>
    </PageTransition>
  );

  if (load.status !== "ready") {
    return shell(
      <p role={load.status === "loading" ? "status" : "alert"} className="rounded-2xl bg-white p-8 text-center text-sm shadow-sm">
        {load.status === "loading" ? "Loading your invoice…" : load.status === "missing" ? "We couldn't find that order." : "We couldn't load this invoice. Check your connection and try again."}{" "}
        <Link to="/orders" className="font-semibold underline">My orders</Link>
      </p>,
    );
  }

  const order = load.order;
  const d = order.delivery;
  const seller = COMPANY_REGISTRATION?.legalName ?? GSTIN_RECORD?.legalEntityName ?? "ZenFresh";
  const taxInvoice = GSTIN_RECORD !== null;
  const paidAt = reachedAt(order, "placed") ?? order.createdAt;

  return shell(
    <>
      <div className="flex flex-wrap items-center justify-between gap-2 print:hidden">
        <Link to={`/orders/${order.ref}`} className="inline-flex min-h-11 items-center gap-2 rounded-full bg-white px-4 text-sm font-bold text-ocean-900 shadow-sm ring-1 ring-ocean-900/10 hover:bg-ocean-700/5">
          <ArrowLeft className="h-4 w-4" aria-hidden="true" /> Back to the order
        </Link>
        <button type="button" onClick={() => window.print()} className="inline-flex min-h-11 items-center gap-2 rounded-full bg-ocean-800 px-5 text-sm font-bold text-white hover:bg-ocean-700 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-600">
          <Printer className="h-4 w-4" aria-hidden="true" /> Print / save as PDF
        </button>
      </div>

      {/* The paper: always light, so it prints the same whatever the site theme. */}
      <article aria-label={`Invoice for order ${order.ref}`} className="rounded-2xl bg-white p-6 text-ocean-950 shadow-sm ring-1 ring-ocean-900/5 print:rounded-none print:p-0 print:shadow-none print:ring-0">
        <header className="flex flex-wrap items-start justify-between gap-4 border-b border-ocean-900/10 pb-4">
          <div>
            <h1 className="font-display text-2xl font-bold">{taxInvoice ? "Tax invoice" : "Order invoice"}</h1>
            <p className="mt-1 text-sm font-semibold">{seller}</p>
            {COMPANY_REGISTRATION?.registeredAddress && <p className="text-xs text-ocean-800/80">{COMPANY_REGISTRATION.registeredAddress}</p>}
            {GSTIN_RECORD && <p className="text-xs text-ocean-800/80">GSTIN: <span className="font-mono">{GSTIN_RECORD.gstin}</span></p>}
            {FSSAI_LICENCES.slice(0, 1).map((licence) => (
              <p key={licence.id} className="text-xs text-ocean-800/80">FSSAI: <span className="font-mono">{licence.licenceNumber}</span></p>
            ))}
          </div>
          <dl className="text-sm">
            <div className="flex justify-between gap-6"><dt className="text-ocean-800/80">Invoice no.</dt><dd className="font-mono font-bold">{order.invoiceNumber ?? "—"}</dd></div>
            <div className="flex justify-between gap-6"><dt className="text-ocean-800/80">Invoice date</dt><dd className="font-semibold">{formatWhen(order.createdAt)}</dd></div>
            <div className="flex justify-between gap-6"><dt className="text-ocean-800/80">Order ID</dt><dd className="font-mono font-bold">{order.ref}</dd></div>
          </dl>
        </header>

        {!taxInvoice && (
          <p className="mt-3 rounded-lg bg-ocean-700/5 px-3 py-2 text-xs text-ocean-800/90">
            ZenFresh&apos;s GST registration details aren&apos;t published yet, so this is an order invoice and payment receipt, not a GST tax invoice.
          </p>
        )}

        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <section aria-labelledby="billed-to">
            <h2 id="billed-to" className="text-xs font-bold uppercase tracking-wide text-ocean-800/80">Billed to / deliver to</h2>
            <p className="mt-1 font-semibold">{d.name}</p>
            <p className="text-sm">{d.phone}</p>
            <p className="text-sm">{d.addressLine}, {d.city} {d.pincode}</p>
          </section>
          <section aria-labelledby="payment-info">
            <h2 id="payment-info" className="text-xs font-bold uppercase tracking-wide text-ocean-800/80">Payment</h2>
            <p className="mt-1 text-sm"><span className="font-semibold">Paid</span> {paidVia(order)}{order.payment ? " (Razorpay)" : ""} on {formatWhen(paidAt)}</p>
            {order.walletAmount > 0 && <p className="text-xs text-ocean-800/80">ZenFresh wallet credit used: {formatInr(order.walletAmount)}</p>}
            {order.payment && (
              <>
                <p className="text-xs text-ocean-800/80">{formatInr(order.total - order.walletAmount)} paid online · payment ID <span className="font-mono">{order.payment.paymentId}</span></p>
                <p className="text-xs text-ocean-800/80">Razorpay order <span className="font-mono">{order.payment.razorpayOrderId}</span></p>
              </>
            )}
          </section>
        </div>

        {/* Phones: one stacked block per line (a 6-column table doesn't fit); wider screens and print: the table. */}
        <ul aria-label="Items on this invoice" className="mt-5 divide-y divide-ocean-900/10 border-y border-ocean-900/10 sm:hidden print:hidden">
          {order.items.map((line, index) => (
            <li key={`${line.id}-${line.pack}`} className="py-3 text-sm">
              <p className="font-semibold">{index + 1}. {line.name}</p>
              <p className="text-ocean-800/85">{packById(line.pack).label} · {line.cut}</p>
              <p className="mt-1 flex justify-between tabular-nums"><span>{line.qty} × {formatInr(line.unitPrice)}</span><span className="font-semibold">{formatInr(line.lineTotal)}</span></p>
            </li>
          ))}
        </ul>

        <div className="mt-5 hidden overflow-x-auto sm:block print:block">
          <table className="w-full text-left text-sm">
            <caption className="sr-only">Items on this invoice</caption>
            <thead>
              <tr className="border-b border-ocean-900/15 text-xs uppercase tracking-wide text-ocean-800/80">
                <th scope="col" className="py-2 pr-2">#</th>
                <th scope="col" className="py-2 pr-2">Item</th>
                <th scope="col" className="py-2 pr-2">Pack · cut</th>
                <th scope="col" className="py-2 pr-2 text-right">Qty</th>
                <th scope="col" className="py-2 pr-2 text-right">Rate</th>
                <th scope="col" className="py-2 text-right">Amount</th>
              </tr>
            </thead>
            <tbody>
              {order.items.map((line, index) => (
                <tr key={`${line.id}-${line.pack}`} className="border-b border-ocean-900/10 align-top">
                  <td className="py-2 pr-2">{index + 1}</td>
                  <td className="py-2 pr-2 font-semibold">{line.name}</td>
                  <td className="py-2 pr-2">{packById(line.pack).label} · {line.cut}</td>
                  <td className="py-2 pr-2 text-right tabular-nums">{line.qty}</td>
                  <td className="py-2 pr-2 text-right tabular-nums">{formatInr(line.unitPrice)}</td>
                  <td className="py-2 text-right tabular-nums">{formatInr(line.lineTotal)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <dl className="ml-auto mt-4 w-full max-w-xs space-y-1.5 text-sm">
          <div className="flex justify-between"><dt>Items (rate includes cut prep)</dt><dd className="tabular-nums">{formatInr(order.subtotal)}</dd></div>
          <div className="flex justify-between"><dt>Delivery fee</dt><dd className="tabular-nums">{order.deliveryFee === 0 ? "FREE" : formatInr(order.deliveryFee)}</dd></div>
          {order.discount > 0 && (
            <div className="flex justify-between"><dt>Welcome coupon{order.couponCode ? ` (${order.couponCode})` : ""}</dt><dd className="tabular-nums">−{formatInr(order.discount)}</dd></div>
          )}
          <div className="flex justify-between border-t border-ocean-900/20 pt-2 text-base font-bold"><dt>Total paid</dt><dd className="tabular-nums">{formatInr(order.total)}</dd></div>
        </dl>

        {order.status === "cancelled" && (
          <p className="mt-4 rounded-lg bg-ocean-700/5 px-3 py-2 text-xs text-ocean-800/90">
            This order was cancelled on {formatWhen(reachedAt(order, "cancelled") ?? order.createdAt)}.{" "}
            {order.refund?.status === "initiated"
              ? `A refund of ${formatInr(order.refund.amountPaise / 100)} was started to the original payment method${order.refund.refundId ? ` (reference ${order.refund.refundId})` : ""}.`
              : order.refund?.status === "credited"
                ? `${formatInr(order.refund.amountPaise / 100)} was credited to the customer's ZenFresh wallet.`
                : order.refund
                  ? "The refund is being processed by ZenFresh."
                  : ""}
            {order.walletRefunded > 0 && ` ${formatInr(order.walletRefunded)} paid from the wallet was returned to the wallet.`}
          </p>
        )}
        <p className="mt-4 text-xs text-ocean-800/70">All amounts are in Indian rupees (₹). Order status: {order.status.replace(/_/g, " ")}.</p>
      </article>
    </>,
  );
}
